import React from 'react';
import {
    FileText,
    Paperclip,
    Layout,
    Edit3,
    Eye,
    CheckCircle2,
    Info
} from 'lucide-react';

const STEPS = {
    ENTRY_TYPE: 'step_1_entry_type',
    ATTACHMENTS: 'step_2_attachments',
    TEMPLATE: 'step_3_template',
    DETAILS: 'step_4_details',
    PREVIEW: 'step_5_preview'
};

const BookkeepingProgressTracker = ({
    activeStep,
    entry,
    onStepClick,
    canNavigateToStep,
    isStepCompleted
}) => {
    const steps = [
        {
            id: 1,
            title: 'Posttyp',
            description: 'Välj typ av bokföringspost',
            icon: FileText,
            key: STEPS.ENTRY_TYPE
        },
        // {
        //     id: 2,
        //     title: 'Bilagor',
        //     description: 'Ladda upp kvitton och dokument',
        //     icon: Paperclip,
        //     key: STEPS.ATTACHMENTS
        // },
        {
            id: 3,
            title: 'Mall',
            description: 'Välj bokföringsmall',
            icon: Layout,
            key: STEPS.TEMPLATE
        },
        {
            id: 4,
            title: 'Detaljer',
            description: 'Fyll i postens information',
            icon: Edit3,
            key: STEPS.DETAILS
        },
        {
            id: 5,
            title: 'Granska',
            description: 'Kontrollera och skapa',
            icon: Eye,
            key: STEPS.PREVIEW
        }
    ];

    const getCurrentStep = () => {
        const step = steps.find(s => s.key === activeStep);
        return step ? step.id : 1;
    };

    const isStepActive = (stepId) => {
        return getCurrentStep() === stepId;
    };

    const isStepAccessible = (stepKey) => {
        return canNavigateToStep(stepKey);
    };

    return (
        <>
            <style>{`
    .progress-tracker {
        background: white;
        border-radius: 12px;
        padding: 2rem;
        margin-bottom: 2rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
    }
    
    .progress-header {
        text-align: center;
        margin-bottom: 2rem;
    }
    
    .progress-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 0.5rem;
    }
    
    .progress-subtitle {
        color: #64748b;
        font-size: 0.875rem;
    }
    
    .steps-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        position: relative;
        margin: 2rem 0;
    }
    
    .progress-line {
        position: absolute;
        top: 30px;
        left: 60px;
        right: 60px;
        height: 2px;
        background: #e5e7eb;
        z-index: 0;
    }
    
    .progress-line-fill {
        height: 100%;
        background: #44814E;
        border-radius: 1px;
        transition: width 0.5s ease;
    }
    
    .step-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        position: relative;
        z-index: 1;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 120px;
        max-width: 140px;
    }
    
    .step-item:hover {
        transform: translateY(-2px);
    }
    
    .step-item.disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    .step-item.disabled:hover {
        transform: none;
    }
    
    .step-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
        position: relative;
        transition: all 0.3s ease;
        border: 3px solid transparent;
    }
    
    .step-circle.completed {
        background: linear-gradient(135deg, #44814E, #36673e);
        color: white;
        box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3);
    }
    
    .step-circle.active {
        background: linear-gradient(135deg, #44814E, #3a6e42);
        color: white;
        box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3);
        border-color: rgba(68, 129, 78, 0.2);
        animation: pulse 2s infinite;
    }
    
    .step-circle.pending {
        background: #f8fafc;
        color: #9ca3af;
        border: 2px solid #e5e7eb;
    }
    
    .step-circle.accessible.pending:hover {
        background: #f1f5f9;
        border-color: #44814E;
        color: #44814E;
    }
    
    @keyframes pulse {
        0% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0.4);
        }
        70% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 10px rgba(68, 129, 78, 0);
        }
        100% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0);
        }
    }
    
    .step-content {
        flex: 1;
    }
    
    .step-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 0.25rem;
    }
    
    .step-description {
        font-size: 0.75rem;
        color: #64748b;
        line-height: 1.4;
    }
    
    .step-item.completed .step-title {
        color: #36673e;
    }
    
    .step-item.active .step-title {
        color: #44814E;
    }
    
    .step-item.completed .step-description {
        color: #3a6e42;
    }
    
    .step-item.active .step-description {
        color: #44814E;
    }
    
    .step-status {
        margin-top: 0.5rem;
        font-size: 0.75rem;
        font-weight: 500;
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        display: inline-block;
    }
    
    .status-completed {
        background: rgba(68, 129, 78, 0.15);
        color: #36673e;
    }
    
    .status-active {
        background: rgba(68, 129, 78, 0.25);
        color: #44814E;
    }
    
    .status-pending {
        background: #f1f5f9;
        color: #64748b;
    }
    
    .status-locked {
        background: #fef2f2;
        color: #dc2626;
    }
    
    /* Mobile responsive */
    @media (max-width: 768px) {
        .steps-container {
            flex-direction: column;
            gap: 1rem;
        }
        
        .progress-line {
            display: none;
        }
        
        .step-item {
            flex-direction: row;
            text-align: left;
            min-width: auto;
            max-width: none;
            width: 100%;
            padding: 1rem;
            background: #f8fafc;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
        }
        
        .step-circle {
            width: 50px;
            height: 50px;
            margin-bottom: 0;
            margin-right: 1rem;
            flex-shrink: 0;
        }
        
        .step-content {
            flex: 1;
        }
        
        .step-status {
            margin-top: 0.25rem;
        }
    }
`}</style>

            <div className="progress-tracker">
                <div className="progress-header">
                    <h3 className="progress-title">Bokföringsprocess</h3>
                    <p className="progress-subtitle">
                        Följ dessa steg för att skapa en korrekt bokföringspost
                    </p>
                </div>

                <div className="steps-container">
                    <div className="progress-line">
                        <div
                            className="progress-line-fill"
                            style={{
                                width: `${Math.max(0, (getCurrentStep() - 1) / (steps.length - 1) * 100)}%`
                            }}
                        />
                    </div>

                    {steps.map((step) => {
                        const completed = isStepCompleted(step.key);
                        const active = isStepActive(step.id);
                        const accessible = isStepAccessible(step.key);

                        return (
                            <div
                                key={step.id}
                                className={`step-item ${completed ? 'completed' : ''} ${active ? 'active' : ''} ${accessible ? 'accessible' : 'disabled'}`}
                                onClick={() => accessible && onStepClick(step.key)}
                            >
                                <div className={`step-circle ${completed ? 'completed' : active ? 'active' : 'pending'} ${accessible ? 'accessible' : ''}`}>
                                    {completed ? (
                                        <CheckCircle2 size={24} />
                                    ) : (
                                        <step.icon size={24} />
                                    )}
                                </div>

                                <div className="step-content">
                                    <h4 className="step-title">{step.title}</h4>
                                    <p className="step-description">{step.description}</p>

                                    <div className={`step-status ${completed ? 'status-completed' : active ? 'status-active' : accessible ? 'status-pending' : 'status-locked'}`}>
                                        {completed ? 'Slutförd' : active ? 'Aktiv' : accessible ? 'Tillgänglig' : 'Låst'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-center mt-3">
                    <small className="text-muted">
                        <Info size={16} className="me-1" />
                        Klicka på tillgängliga steg för att navigera direkt dit
                    </small>
                </div>
            </div>
        </>
    );
};

export default BookkeepingProgressTracker;